<template>
    <v-form @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <CardHeader :title="title" :showBackButton="true" />

            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="12">
                        <v-radio-group v-model="legal_person" row>
                            <v-card outlined class="mr-2">
                                <v-card-actions class="pa-5">
                                    <v-radio
                                        :label="
                                            $t(
                                                'customer.form.labels.legal-person',
                                            )
                                        "
                                        :value="true"
                                    ></v-radio>
                                </v-card-actions>
                            </v-card>
                            <v-card outlined>
                                <v-card-actions class="pa-5">
                                    <v-radio
                                        :label="
                                            $t(
                                                'customer.form.labels.normal-person',
                                            )
                                        "
                                        :value="false"
                                    ></v-radio>
                                </v-card-actions>
                            </v-card>
                        </v-radio-group>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            flat
                            v-model="customer.trade_name"
                            :label="
                                legal_person
                                    ? $t('customer.form.labels.trade-name')
                                    : $t('customer.form.labels.name')
                            "
                            :counter="180"
                            :error-messages="tradeNameErrors"
                            @input="$v.customer.trade_name.$touch()"
                            @blur="$v.customer.trade_name.$touch()"
                            autofocus
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="customer.legal_name"
                            :label="$t('customer.form.labels.legal_name')"
                            :counter="180"
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="!legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="customer.cpf"
                            :label="$t('customer.form.labels.cpf')"
                            v-mask="'###.###.###-##'"
                            :counter="14"
                            :error-messages="cpfErrors"
                            @input="$v.customer.cpf.$touch()"
                            @blur="$v.customer.cpf.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="customer.cnpj"
                            :label="$t('customer.form.labels.cnpj')"
                            v-mask="'XX.XXX.XXX/XXXX-##'"
                            :counter="18"
                            :error-messages="cnpjErrors"
                             @input="handleCnpjInput"
                            @blur="$v.customer.cnpj.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            flat
                            v-model="customer.email"
                            :label="$t('customer.form.labels.email')"
                            :error-messages="emailErrors"
                            @input="$v.customer.email.$touch()"
                            @blur="$v.customer.email.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            flat
                            v-model="customer.phone"
                            :label="$t('customer.form.labels.phone')"
                            v-mask="['(##) ####-####', '(##) #####-####']"
                            :error-messages="phoneErrors"
                            @input="$v.customer.phone.$touch()"
                            @blur="$v.customer.phone.$touch()"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            flat
                            v-model="customer.readable_status"
                            readonly
                            disabled
                            :label="$t('resources.customer.attributes.status')"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-select
                            v-model="customer.seller_id"
                            :items="sellers"
                            label="Vendedor"
                            clearable
                            item-text="name"
                            item-value="id"
                        ></v-select>
                    </v-col>
                </v-row>
            </v-card-text>

            <form-actions :loadingSave="loading">
                <template #left>
                    <resource-actions
                        v-if="customer.id"
                        :model="customer"
                        pageContext="edit"
                        @click="$refs.actionHandler[$event.type](customer)"
                    />
                </template>
            </form-actions>
        </v-card>

        <CustomerActions @modelChanged="reloadPage()" ref="actionHandler" />
    </v-form>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { mask } from 'vue-the-mask'
import CardHeader from '@/shared/components/CardHeader'
import CustomerActions from '@/components/actions/customer/CustomerActions'
import FormActions from '@/shared/components/FormActions'
import {
    required,
    minLength,
    maxLength,
    email,
    requiredIf,
} from 'vuelidate/lib/validators'
import ResourceActions from '@/shared/components/ResourceActions'
import User from '@/models/User'

export default {
    props: ['customer', 'titlePrefix', 'type'],

    data: () => {
        return {
            loading: false,
            legal_person: true,
            sellers: [],
        }
    },

    components: {
        CardHeader,
        CustomerActions,
        FormActions,
        ResourceActions,
    },

    directives: { mask },

    mixins: [validationMixin],

    validations() {
        return {
            customer: {
                trade_name: { required, maxLength: maxLength(180) },
                cpf: {
                    required: requiredIf(customer => {
                        return !customer.cnpj
                    }),
                    minLength: minLength(14),
                },
                cnpj: {
                    required: requiredIf(customer => {
                        return !customer.cpf
                    }),
                    minLength: minLength(18),
                },
                email: { required, email },
                phone: {
                    required,
                    minLength: minLength(14),
                },
            },
        }
    },

    methods: {
        recaptcha() {
            return this.$recaptcha('customer')
        },

        reloadPage() {
            window.location.reload()
        },

        async fetchSellers() {
            let res = await new User().all()
            if (!res) return
            this.sellers = res.data
        },

        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('customer.form.bus.save-error'),
                )

                this.loading = false
                return
            }

            if (this.type === 'create') {
                let recaptchaToken = await this.recaptcha()

                if (!recaptchaToken) {
                    this.$bus.$emit(
                        'notify',
                        'error',
                        this.$t('customer.form.bus.save-recaptcha-error'),
                    )

                    this.loading = false
                    return
                }

                this.customer.recaptcha = recaptchaToken
            }

            let clone = this.customer.clone()
            let res = null

            try {
                res = await clone.save().catch(err => {
                    err = err.response
                    if (err && err.status === 403) {
                        this.$bus.$emit('notify', 'error', err.data.message)
                    }
                })
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('customer.form.bus.save-success', {
                    client: clone.trade_name,
                }),
            )

            this.$emit('saved')
        },

        handleCnpjInput(value) {
            this.customer.cnpj = value ? value.toUpperCase() : value
            this.$v.customer.cnpj.$touch()
        },
    },

    computed: {
        title() {
            return this.$t('forms.title', {
                prefix: this.titlePrefix,
                resource: this.$tc('resources.customer.name', 1),
            })
        },

        tradeNameErrors() {
            const errors = []
            if (!this.$v.customer.trade_name.$dirty) return errors

            !this.$v.customer.trade_name.maxLength &&
                errors.push(
                    this.$t('customer.form.messages.name-error-max-length'),
                )

            !this.$v.customer.trade_name.required &&
                errors.push(
                    this.$t('customer.form.messages.name-error-required'),
                )
            return errors
        },

        cpfErrors() {
            const errors = []
            if (!this.$v.customer.cpf.$dirty) return errors

            !this.$v.customer.cpf.required &&
                errors.push(
                    this.$t('customer.form.messages.cpf-error-required'),
                )

            !this.$v.customer.cpf.minLength &&
                errors.push(
                    this.$t('customer.form.messages.cpf-error-min-length'),
                )

            return errors
        },

        cnpjErrors() {
            const errors = []
            if (!this.$v.customer.cnpj.$dirty) return errors

            !this.$v.customer.cnpj.required &&
                errors.push(
                    this.$t('customer.form.messages.cnpj-error-required'),
                )

            !this.$v.customer.cnpj.minLength &&
                errors.push(
                    this.$t('customer.form.messages.cnpj-error-min-length'),
                )

            return errors
        },

        emailErrors() {
            const errors = []
            if (!this.$v.customer.email.$dirty) return errors

            !this.$v.customer.email.required &&
                errors.push(
                    this.$t('customer.form.messages.email-error-required'),
                )

            !this.$v.customer.email.email &&
                errors.push(this.$t('customer.form.messages.email-error-email'))

            return errors
        },

        phoneErrors() {
            const errors = []
            if (!this.$v.customer.phone.$dirty) return errors

            !this.$v.customer.phone.required &&
                errors.push(
                    this.$t('customer.form.messages.phone-error-required'),
                )

            !this.$v.customer.phone.minLength &&
                errors.push(
                    this.$t('customer.form.messages.phone-error-min-length'),
                )

            return errors
        },
    },

    created() {
        if (this.type == 'edit' && this.customer.cpf) {
            this.legal_person = false
        }
        this.fetchSellers()
    },
}
</script>
